import { useEffect, useRef, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { X, MapPin, Phone, Globe, Verified, Star, Navigation, SlidersHorizontal } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Slider } from '@/components/ui/slider';
import { resolveImageUrl } from '@/utils/imageUrl';
import { OptimizedImage } from '@/components/OptimizedImage';

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN;

mapboxgl.accessToken = MAPBOX_TOKEN;

const API = `${window.location.origin}/api`;

export default function MapView({ category, onClose }) {
  const mapContainer = useRef(null);
  const map = useRef(null);
  const markersRef = useRef([]);
  const [businesses, setBusinesses] = useState([]);      // geocoded → map pins
  const [nearbyList, setNearbyList] = useState([]);      // no coords → sidebar list
  const [userState, setUserState] = useState(null);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [locationError, setLocationError] = useState(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [radius, setRadius] = useState(25);
  const [showFilters, setShowFilters] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);

  // Request user location
  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser');
      // Default to center of US
      setUserLocation({ lat: 39.8283, lng: -98.5795 });
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setUserLocation({ lat: latitude, lng: longitude });
        setLocationError(null);
      },
      (error) => {
        console.error('Geolocation error:', error);
        setLocationError('Unable to get your location. Showing all businesses.');
        // Default to center of US
        setUserLocation({ lat: 39.8283, lng: -98.5795 });
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  // Fetch nearby businesses when location is available
  useEffect(() => {
    if (!userLocation) return;

    const fetchBusinesses = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          lat: userLocation.lat.toString(),
          lng: userLocation.lng.toString(),
          radius: radius.toString(),
          limit: '50',
        });
        if (category) params.append('category', category);

        const response = await fetch(`${API}/businesses/nearby?${params.toString()}`);
        if (response.ok) {
          const data = await response.json();
          setBusinesses(data.businesses || []);
          setNearbyList(data.nearby_list || []);
          setUserState(data.user_state || null);
          // Auto-show sidebar if no map pins but there are nearby businesses
          if ((data.businesses || []).length === 0 && (data.nearby_list || []).length > 0) {
            setShowSidebar(true);
          }
        }
      } catch (error) {
        console.error('Error fetching businesses:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBusinesses();
  }, [userLocation, category, radius]);

  // Initialize map
  useEffect(() => {
    if (!mapContainer.current || !userLocation || map.current) return;

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/mapbox/dark-v11',
      center: [userLocation.lng, userLocation.lat],
      zoom: 10,
    });

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

    // Add geolocate control
    const geolocate = new mapboxgl.GeolocateControl({
      positionOptions: { enableHighAccuracy: true },
      trackUserLocation: true,
      showUserHeading: true
    });
    map.current.addControl(geolocate, 'top-right');

    map.current.on('load', () => {
      setMapLoaded(true);
    });

    return () => {
      map.current?.remove();
      map.current = null;
    };
  }, [userLocation]);

  // Add markers when businesses or map changes
  useEffect(() => {
    if (!map.current || !mapLoaded || businesses.length === 0) return;

    // Clear existing markers
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    // Add markers for each business
    businesses.forEach((business) => {
      if (!business.latitude || !business.longitude) return;

      // Create custom marker element
      const el = document.createElement('div');
      el.className = 'business-marker';
      el.innerHTML = `
        <div class="marker-pin">
          <svg xmlns="http://www.w3.org/2000/svg" width="32" height="40" viewBox="0 0 24 30" fill="none">
            <path d="M12 0C5.4 0 0 5.4 0 12C0 21 12 30 12 30S24 21 24 12C24 5.4 18.6 0 12 0Z" fill="#FFFFFF"/>
            <circle cx="12" cy="12" r="6" fill="#050505"/>
          </svg>
        </div>
      `;
      el.style.cursor = 'pointer';
      el.style.width = '32px';
      el.style.height = '40px';

      el.addEventListener('click', () => {
        setSelectedBusiness(business);
        map.current.flyTo({
          center: [business.longitude, business.latitude],
          zoom: 14,
          duration: 1000
        });
      });

      const marker = new mapboxgl.Marker(el)
        .setLngLat([business.longitude, business.latitude])
        .addTo(map.current);

      markersRef.current.push(marker);
    });

    // Fit bounds to show all markers
    if (businesses.length > 1) {
      const bounds = new mapboxgl.LngLatBounds();
      businesses.forEach(biz => {
        if (biz.latitude && biz.longitude) {
          bounds.extend([biz.longitude, biz.latitude]);
        }
      });
      map.current.fitBounds(bounds, { padding: 50, maxZoom: 12 });
    }
  }, [businesses, mapLoaded]);

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-3 h-3 ${
              star <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#050505]">
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-gradient-to-b from-[#050505] to-transparent p-4 md:p-6">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white">
              {category ? `${category} Near You` : 'Businesses Near You'}
            </h2>
            {locationError && (
              <p className="text-yellow-400 text-sm mt-1">{locationError}</p>
            )}
            {!locationError && userLocation && (
              <p className="text-zinc-400 text-sm mt-1">
                {businesses.length > 0
                  ? `${businesses.length} on map`
                  : `No map pins yet`}
                {nearbyList.length > 0 && ` · ${nearbyList.length} in ${userState || 'your area'} (no coords)`}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {nearbyList.length > 0 && (
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className={`px-3 py-2 rounded-full text-sm font-medium backdrop-blur-sm border transition-colors ${
                  showSidebar
                    ? 'bg-white text-black border-white'
                    : 'bg-zinc-900/80 text-white border-zinc-800 hover:bg-zinc-800'
                }`}
                data-testid="toggle-sidebar"
              >
                {showSidebar ? 'Hide List' : `List (${nearbyList.length})`}
              </button>
            )}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`w-10 h-10 rounded-full backdrop-blur-sm border flex items-center justify-center transition-colors ${
                showFilters 
                  ? 'bg-white text-black border-white' 
                  : 'bg-zinc-900/80 text-white border-zinc-800 hover:bg-zinc-800'
              }`}
              data-testid="toggle-map-filters"
            >
              <SlidersHorizontal className="w-5 h-5" />
            </button>
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-zinc-900/80 backdrop-blur-sm border border-zinc-800 flex items-center justify-center text-white hover:bg-zinc-800 transition-colors"
              data-testid="close-map-button"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Distance Filter Panel */}
        <AnimatePresence>
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="max-w-7xl mx-auto mt-4"
            >
              <div className="bg-zinc-900/90 backdrop-blur-sm border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-zinc-400 shrink-0">
                    <Navigation className="w-4 h-4" />
                    <span className="text-sm font-medium">Distance</span>
                  </div>
                  <div className="flex-1">
                    <Slider
                      value={[radius]}
                      onValueChange={(values) => setRadius(values[0])}
                      min={1}
                      max={100}
                      step={1}
                      className="w-full"
                      data-testid="distance-slider"
                    />
                  </div>
                  <div className="bg-zinc-800 px-3 py-1 rounded-lg shrink-0">
                    <span className="text-white font-mono text-sm">{radius} mi</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Map Container */}
      <div ref={mapContainer} className="w-full h-full" />

      {/* Sidebar: businesses without geocoded coordinates */}
      <AnimatePresence>
        {showSidebar && nearbyList.length > 0 && (
          <motion.div
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="absolute top-0 right-0 bottom-0 w-80 z-10 bg-[#0A0A0A]/95 backdrop-blur-sm border-l border-zinc-800 overflow-y-auto"
          >
            <div className="p-4 pt-20 space-y-3">
              <p className="text-xs text-zinc-500 pb-1 border-b border-zinc-800">
                {nearbyList.length} businesses in {userState} — not yet mapped (geocoding pending)
              </p>
              {nearbyList.map((biz) => (
                <Link
                  key={biz.business_id}
                  to={`/businesses/${biz.business_id}`}
                  className="block p-3 rounded-xl bg-zinc-900 border border-zinc-800 hover:border-zinc-600 transition-colors"
                >
                  <p className="text-white font-medium text-sm truncate">{biz.name}</p>
                  <p className="text-zinc-500 text-xs mt-0.5">{[biz.city, biz.state].filter(Boolean).join(', ')}</p>
                  {biz.category && <span className="text-xs px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 mt-1 inline-block">{biz.category}</span>}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading Overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#050505]/80 z-20">
          <div className="text-center">
            <div className="w-10 h-10 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-zinc-400">Loading nearby businesses...</p>
          </div>
        </div>
      )}

      {/* Business Detail Drawer */}
      <AnimatePresence>
        {selectedBusiness && (
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="absolute bottom-0 left-0 right-0 z-20 max-h-[60vh] overflow-hidden"
          >
            <div className="bg-[#0A0A0A] border-t border-zinc-800 rounded-t-2xl">
              {/* Handle */}
              <div className="flex justify-center py-3">
                <div className="w-12 h-1 rounded-full bg-zinc-700" />
              </div>

              {/* Content */}
              <div className="px-6 pb-8 overflow-y-auto max-h-[calc(60vh-50px)]">
                {/* Close Button */}
                <button
                  onClick={() => setSelectedBusiness(null)}
                  className="absolute top-4 right-4 w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white transition-colors"
                  data-testid="close-drawer-button"
                >
                  <X className="w-4 h-4" />
                </button>

                {/* Business Image */}
                {selectedBusiness.image_url && (
                  <div className="relative aspect-video rounded-lg overflow-hidden mb-4 -mx-2">
                    <OptimizedImage
                      src={selectedBusiness.image_url}
                      alt={selectedBusiness.name}
                      preset="card"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                  </div>
                )}

                {/* Verified Badge & Distance */}
                <div className="flex items-center gap-3 mb-3">
                  {selectedBusiness.verification_status === 'verified' && (
                    <span className="verified-badge">
                      <Verified className="w-3 h-3" />
                      Verified
                    </span>
                  )}
                  {selectedBusiness.distance && (
                    <span className="flex items-center gap-1 text-zinc-400 text-sm">
                      <Navigation className="w-3 h-3" />
                      {selectedBusiness.distance} mi
                    </span>
                  )}
                </div>

                {/* Name & Category */}
                <p className="category-tag mb-2">{selectedBusiness.category}</p>
                <h3 className="text-2xl font-bold text-white mb-2">
                  {selectedBusiness.name}
                </h3>

                {/* Rating */}
                {selectedBusiness.review_count > 0 && (
                  <div className="flex items-center gap-2 mb-4">
                    {renderStars(Math.round(selectedBusiness.average_rating))}
                    <span className="text-zinc-400 text-sm">
                      {selectedBusiness.average_rating.toFixed(1)} ({selectedBusiness.review_count} reviews)
                    </span>
                  </div>
                )}

                {/* Address */}
                <div className="flex items-start gap-3 text-zinc-400 text-sm mb-4">
                  <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                  <span>
                    {[selectedBusiness.address, selectedBusiness.city, selectedBusiness.state, selectedBusiness.zip_code]
                      .filter(Boolean)
                      .join(', ')}
                  </span>
                </div>

                {/* Description */}
                <p className="text-zinc-400 text-sm mb-6 line-clamp-3">
                  {selectedBusiness.description}
                </p>

                {/* Actions */}
                <div className="flex gap-3">
                  <Link
                    to={`/businesses/${selectedBusiness.business_id}`}
                    className="flex-1 btn-primary text-center py-3"
                    data-testid="view-business-button"
                  >
                    View Details
                  </Link>
                  
                  {selectedBusiness.phone && (
                    <a
                      href={`tel:${selectedBusiness.phone}`}
                      className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
                      data-testid="call-business-button"
                    >
                      <Phone className="w-5 h-5" />
                    </a>
                  )}
                  
                  {selectedBusiness.website && (
                    <a
                      href={selectedBusiness.website.startsWith('http') ? selectedBusiness.website : `https://${selectedBusiness.website}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center text-white hover:bg-zinc-700 transition-colors"
                      data-testid="website-business-button"
                    >
                      <Globe className="w-5 h-5" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Marker Style */}
      <style>{`
        .business-marker {
          transition: transform 0.2s ease;
        }
        .business-marker:hover {
          transform: scale(1.1);
        }
        .mapboxgl-ctrl-group {
          background: #0A0A0A !important;
          border: 1px solid #27272A !important;
        }
        .mapboxgl-ctrl-group button {
          background-color: transparent !important;
        }
        .mapboxgl-ctrl-group button:hover {
          background-color: #27272A !important;
        }
        .mapboxgl-ctrl-icon {
          filter: invert(1);
        }
        .mapboxgl-user-location-dot {
          background-color: #00FF94 !important;
        }
        .mapboxgl-user-location-accuracy-circle {
          background-color: rgba(0, 255, 148, 0.2) !important;
        }
      `}</style>
    </div>
  );
}
